import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@shared/components/ui/button';
import {
  User,
  FileText,
  Users,
  BadgeCheck,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  ShieldCheck,
  Clock,
  Loader2,
  UserCheck,
  Send,
} from 'lucide-react';
import { supabase } from '@shared/supabaseClient';
import { useToast } from '@shared/components/ui/use-toast';
import { ProjectTabBar } from '@/components/portal/PortalUI';

/**
 * Project-scoped Setup Guide.
 * Evaluates all steps against a specific project — no cross-project confusion.
 *
 * Lead steps  (6): Profile → Induction → Add Team → Team Inducted → Submit → Badge
 * Member steps (3): Profile → Induction → Badge
 */

const PortalProjectSetupGuide = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { session } = useOutletContext();
  const { toast } = useToast();

  const email = session?.person?.email;

  const [project, setProject]                   = useState(null);
  const [isLead, setIsLead]                     = useState(false);
  const [loading, setLoading]                   = useState(true);
  const [expandedStep, setExpandedStep]         = useState(null);
  const [inductionSubmissionId, setInductionSubmissionId] = useState(null);
  const [submittingProject, setSubmittingProject] = useState(false);
  const [completionStatus, setCompletionStatus] = useState({
    profile:        false,
    induction:      false,
    team:           false,
    teamInductions: false,
    submitted:      false,
    badge:          false,
  });

  useEffect(() => {
    if (email && projectId) loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, projectId]);

  const loadAll = async () => {
    setLoading(true);
    try {
      // ── Load project ──────────────────────────────────────────────────────
      const { data: proj, error: projErr } = await supabase
        .from('projects')
        .select('*, depots:site_id(id, name)')
        .eq('id', projectId)
        .single();

      if (projErr || !proj) throw new Error('Project not found');
      setProject(proj);

      const lead = proj.project_lead_email?.toLowerCase().trim() === email?.toLowerCase().trim();
      setIsLead(lead);

      // ── Profile ───────────────────────────────────────────────────────────
      const { data: profile } = await supabase
        .from('portal_profiles')
        .select('person_name, person_phone, person_company, photo_url, vehicle_reg, attending_site_id, profile_completed_at')
        .eq('person_email', email)
        .maybeSingle();

      const profileDone = !!(
        profile?.person_name &&
        profile?.person_phone &&
        profile?.person_company &&
        profile?.photo_url &&
        profile?.vehicle_reg &&
        profile?.attending_site_id &&
        profile?.profile_completed_at
      );

      // ── Induction for this project's site ─────────────────────────────────
      // Load active briefs WITH their current version, then verify the person
      // has a submission at that exact version (not just any old submission).
      let inductionDone = false;
      try {
        const { data: briefs } = await supabase
          .from('induction_briefs')
          .select('id, version')
          .eq('depot_id', proj.site_id)
          .eq('is_active', true);

        if (!briefs || briefs.length === 0) {
          inductionDone = true; // No active briefs for this site — nothing to do
        } else {
          // Build a map of brief_id → current version
          const briefVersionMap = Object.fromEntries(briefs.map(b => [b.id, b.version]));

          const { data: history } = await supabase
            .rpc('portal_get_submission_history', { p_email: email });

          // For every active brief, the person must have a submitted/approved
          // submission whose brief_version matches the brief's CURRENT version.
          inductionDone = briefs.every(brief => {
            return (history || []).some(s =>
              s.brief_id      === brief.id &&
              s.brief_version === briefVersionMap[brief.id] &&
              (s.status === 'submitted' || s.status === 'approved')
            );
          });
        }
      } catch (e) {
        console.error('[ProjectSetupGuide] induction check failed', e);
      }

      // ── Team members (lead only) ─────────────────────────────────────────
      let teamDone = false;
      let teamInductionsDone = false;
      if (lead) {
        const { data: members } = await supabase
          .from('project_team_members')
          .select('id, person_email')
          .eq('project_id', projectId)
          .neq('person_email', email);
        teamDone = (members?.length || 0) > 0;

        // Team inductions via RPC
        if (teamDone) {
          try {
            const { data: summary } = await supabase
              .rpc('portal_get_project_induction_summary', { p_lead_email: email });
            const row = (summary || []).find(s => s.project_id === projectId);
            teamInductionsDone = row?.all_inducted === true;
          } catch (e) {
            // RPC not deployed yet — safe to leave teamInductionsDone as false
          }
        }

        // Also fetch the lead's own induction submission so "Start Induction"
        // links directly to their brief instead of the generic invites list.
        try {
          const { data: sub } = await supabase
            .from('induction_submissions')
            .select('id')
            .ilike('person_email', email)
            .eq('depot_id', proj.site_id)
            .in('status', ['invited', 'in_progress', 'submitted', 'approved', 'pending_acknowledgement'])
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();
          if (sub?.id) setInductionSubmissionId(sub.id);
        } catch (e) {
          // non-critical — falls back to generic inductions list
        }
      } else {
        // Team members skip these steps
        teamDone          = true;
        teamInductionsDone = true;

        // Fetch this team member's induction_submission_id so we can link
        // directly to their brief rather than the generic inductions list.
        try {
          // Primary: project_team_members.induction_submission_id (if backfilled)
          const { data: ptmRow } = await supabase
            .from('project_team_members')
            .select('induction_submission_id')
            .eq('project_id', projectId)
            .ilike('person_email', email)
            .not('induction_submission_id', 'is', null)
            .maybeSingle();

          if (ptmRow?.induction_submission_id) {
            setInductionSubmissionId(ptmRow.induction_submission_id);
          } else {
            // Fallback: find the submission directly by email + depot (site_id)
            // This handles cases where induction_submission_id wasn't backfilled
            // on the project_team_members row.
            const { data: sub } = await supabase
              .from('induction_submissions')
              .select('id')
              .ilike('person_email', email)
              .eq('depot_id', proj.site_id)
              .in('status', ['invited', 'in_progress', 'submitted', 'approved', 'pending_acknowledgement'])
              .order('created_at', { ascending: false })
              .limit(1)
              .maybeSingle();
            if (sub?.id) setInductionSubmissionId(sub.id);
          }
        } catch (e) {
          // non-critical — falls back to generic inductions list
        }
      }

      // ── Submitted ────────────────────────────────────────────────────────
      const submittedDone = ['submitted_awaiting_approval', 'approved', 'active'].includes(proj.status);

      // ── Badge ────────────────────────────────────────────────────────────
      // Only mark as done when the user actually has an active/suspended pass
      // for this project — NOT just when the project status is 'approved'.
      let badgeDone = false;
      try {
        // 1. Check passes with matching project_id
        const { data: projectPasses } = await supabase
          .from('passes')
          .select('id')
          .ilike('person_email', email)
          .eq('project_id', projectId)
          .in('status', ['active', 'suspended'])
          .limit(1);

        if (projectPasses && projectPasses.length > 0) {
          badgeDone = true;
        } else {
          // 2. Fallback: check via project_team_members pass_id
          const { data: ptmPass } = await supabase
            .from('project_team_members')
            .select('pass_id')
            .eq('project_id', projectId)
            .ilike('person_email', email)
            .not('pass_id', 'is', null)
            .limit(1);
          badgeDone = (ptmPass && ptmPass.length > 0);
        }
      } catch (e) {
        console.error('[ProjectSetupGuide] badge check failed', e);
      }

      const status = {
        profile:        profileDone,
        induction:      inductionDone,
        team:           teamDone,
        teamInductions: teamInductionsDone,
        submitted:      submittedDone,
        badge:          badgeDone,
      };
      setCompletionStatus(status);

      // Auto-expand first incomplete step
      const steps = getSteps(lead, projectId, inductionSubmissionId);
      const firstIncomplete = steps.find(s => !status[s.id]);
      if (firstIncomplete) setExpandedStep(firstIncomplete.id);
    } catch (err) {
      console.error('[ProjectSetupGuide] loadAll failed:', err);
    } finally {
      setLoading(false);
    }
  };

  // ── Submit project for review (runs inline — no navigation) ─────────────
  const handleSubmitProject = useCallback(async () => {
    if (!project || submittingProject) return;
    setSubmittingProject(true);
    try {
      const isUpdate = project.status === 'submitted_awaiting_approval';
      const updatePayload = {
        submitted_at: new Date().toISOString(),
        submitted_by_email: email,
      };
      if (!isUpdate) {
        updatePayload.status = 'submitted_awaiting_approval';
      }

      const { error } = await supabase
        .from('projects')
        .update(updatePayload)
        .eq('id', projectId);

      if (error) throw error;

      // Best-effort admin notification
      if (project.created_by_admin_email) {
        try {
          await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notify-project-submitted`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
            },
            body: JSON.stringify({
              project_id: projectId,
              project_name: project.project_name,
              admin_email: project.created_by_admin_email,
              lead_name: project.project_lead_name,
              lead_email: project.project_lead_email,
              site_name: project.depots?.name || 'Unknown site',
              is_update: isUpdate,
            }),
          });
        } catch (_) { /* non-critical */ }
      }

      toast({
        title: isUpdate ? 'Project Resubmitted' : 'Project Submitted',
        description: isUpdate
          ? 'Your project updates have been resubmitted for review.'
          : 'Your project has been submitted for review. The site team will be notified.',
      });

      // Reload data so step 5 shows as complete
      await loadAll();
    } catch (err) {
      console.error('[ProjectSetupGuide] submit failed:', err);
      toast({
        title: 'Submission Failed',
        description: 'Could not submit the project. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSubmittingProject(false);
    }
  }, [project, projectId, email, submittingProject, toast]);

  const getSteps = (lead, pid, submId) => {
    const base = [
      {
        id: 'profile',
        icon: User,
        title: 'Complete Your Profile',
        description: 'Your name, company, photo, phone, and vehicle registration must be on file before you can access site.',
        action: 'Set Up Profile',
        route: '/portal/profile',
        why: 'Required for site access',
        time: '2 minutes',
      },
      {
        id: 'induction',
        icon: FileText,
        title: 'Complete Site Induction',
        description: `Read and complete the induction for ${project?.depots?.name || 'this site'}. Covers safety, PPE, emergency procedures and site rules.`,
        action: 'Start Induction',
        route: submId
          ? `/portal/inductions/${submId}/brief`
          : '/portal/inductions',
        why: 'Legally required before entering site',
        time: '5–10 minutes',
      },
    ];

    if (lead) {
      return [
        ...base,
        {
          id: 'team',
          icon: Users,
          title: 'Add Your Team Members',
          description: 'Invite everyone working with you on this project. Each person will receive their own portal invite to complete their induction.',
          action: 'Add Team',
          route: `/portal/projects/${pid}/team`,
          openInviteDialog: true,
          why: 'All site workers need their own pass',
          time: '2 minutes per person',
        },
        {
          id: 'teamInductions',
          icon: UserCheck,
          title: 'Await Team Inductions',
          description: 'Your team members must each complete their site induction. You will move to the next step automatically once everyone is inducted.',
          action: 'View Progress',
          route: `/portal/projects/${pid}/team`,
          why: 'All site workers must be inducted',
          time: 'Depends on your team',
        },
        {
          id: 'submitted',
          icon: Send,
          title: 'Submit Project for Review',
          description: 'Once all inductions are complete, submit your project for the site team to review. They will check all documentation and induction records.',
          action: 'Submit for Review',
          onAction: handleSubmitProject,
          why: 'Site team must sign off before work begins',
          time: '2 minutes',
        },
        {
          id: 'badge',
          icon: BadgeCheck,
          title: 'Receive Digital Site Pass',
          description: 'Once the site team approves your project, everyone on the team receives their digital QR pass automatically. Present it at the gate kiosk.',
          action: 'View Badge',
          route: '/portal/digital-badge',
          why: 'Your site access credential',
          time: 'Once project approved',
        },
      ];
    }

    // Team member — 3 steps
    return [
      ...base,
      {
        id: 'badge',
        icon: BadgeCheck,
        title: 'Receive Digital Site Pass',
        description: 'Once the site team approves your project, your digital QR pass is issued automatically. Present it at the gate kiosk on arrival.',
        action: 'View Badge',
        route: '/portal/digital-badge',
        why: 'Your site access credential',
        time: 'Once project approved',
      },
    ];
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const steps = getSteps(isLead, projectId, inductionSubmissionId);
  const completedCount = steps.filter(s => completionStatus[s.id]).length;
  const allDone = steps.every(s => completionStatus[s.id]);

  return (
    <>
      <ProjectTabBar projectId={projectId} isProjectLead={isLead} />
      <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">

      {/* Header */}
      <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <div className="flex items-center gap-2 mb-4">
          <div className="p-1.5 rounded-lg bg-primary/10">
            <ShieldCheck className="h-4 w-4 text-primary" />
          </div>
          <span className="text-xs font-bold text-primary uppercase tracking-widest">
            {project?.project_name}
          </span>
        </div>
        <h1 className="text-2xl font-bold text-foreground mb-1">Setup Guide</h1>
        <p className="text-sm font-medium text-muted-foreground">
          {project?.depots?.name} · {isLead ? 'Project Lead' : 'Team Member'}
        </p>

        {/* Progress bar */}
        <div className="mt-4 p-4 rounded-xl border border-border bg-card">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="font-semibold text-foreground">Progress</span>
            <span className="font-bold text-primary">{completedCount} / {steps.length} complete</span>
          </div>
          <div className="h-2.5 bg-muted rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${(completedCount / steps.length) * 100}%` }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              className="h-full rounded-full bg-primary"
            />
          </div>
        </div>
      </motion.div>

      {/* All-done banner */}
      <AnimatePresence>
        {allDone && (
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="p-5 rounded-xl bg-green-50 border border-green-300 flex items-center gap-4"
          >
            <div className="p-2.5 rounded-full bg-green-100 flex-shrink-0">
              <CheckCircle2 className="h-6 w-6 text-green-700" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-green-900">All set for {project?.project_name}!</p>
              <p className="text-sm font-medium text-green-800">Your project has been approved and your digital site pass has been issued.</p>
            </div>
            <Button onClick={() => navigate('/portal/digital-badge')} className="bg-green-700 hover:bg-green-800 text-white flex-shrink-0">
              View Pass <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Steps */}
      <div className="space-y-2">
        {steps.map((step, index) => {
          const done       = completionStatus[step.id];
          const isExpanded = expandedStep === step.id;
          const Icon       = step.icon;
          const locked     = index > 0 && !completionStatus[steps[index - 1].id];
          const displayNum = index + 1;

          return (
            <motion.div
              key={step.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, delay: index * 0.04 }}
            >
              <div className={`rounded-xl border overflow-hidden transition-colors duration-200
                ${ done    ? 'border-green-300 bg-green-50'
                : locked   ? 'border-border bg-muted/30 opacity-60'
                           : 'border-primary/40 bg-card shadow-sm' }`}
              >
                <button
                  type="button"
                  className="w-full text-left px-4 py-3.5 flex items-center gap-3 disabled:cursor-not-allowed"
                  onClick={() => !locked && setExpandedStep(isExpanded ? null : step.id)}
                  disabled={locked}
                >
                  <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center
                    ${ done ? 'bg-green-100' : locked ? 'bg-muted' : 'bg-primary/10' }`}
                  >
                    {done
                      ? <CheckCircle2 className="h-5 w-5 text-green-700" />
                      : <Icon className={`h-4 w-4 ${locked ? 'text-muted-foreground' : 'text-primary'}`} />
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className={`text-xs font-bold uppercase tracking-wider
                        ${ done ? 'text-green-700' : 'text-muted-foreground' }`}>
                        Step {displayNum}
                      </span>
                      {done && (
                        <span className="inline-flex items-center rounded-full bg-green-100 border border-green-300 px-2 py-0 text-xs font-bold text-green-800">
                          Complete
                        </span>
                      )}
                      {!done && !locked && (
                        <span className="inline-flex items-center rounded-full bg-primary/10 border border-primary/30 px-2 py-0 text-xs font-bold text-primary">
                          Action needed
                        </span>
                      )}
                      {!done && locked && (
                        <span className="inline-flex items-center rounded-full bg-muted border border-border px-2 py-0 text-xs font-semibold text-muted-foreground">
                          Complete step {displayNum - 1} first
                        </span>
                      )}
                    </div>
                    <p className={`font-bold text-sm ${ done ? 'text-muted-foreground line-through' : 'text-foreground' }`}>
                      {step.title}
                    </p>
                  </div>
                  {!locked && (
                    isExpanded
                      ? <ChevronUp className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      : <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  )}
                </button>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateRows: isExpanded && !locked ? '1fr' : '0fr',
                    transition: 'grid-template-rows 0.18s ease',
                  }}
                >
                  <div className="overflow-hidden">
                      <div className="px-4 pb-4 pt-0 border-t border-border">
                        <p className="text-sm font-medium text-muted-foreground leading-relaxed mt-3 mb-4">
                          {step.description}
                        </p>
                        <div className="flex items-center gap-5 mb-4 text-xs font-semibold text-muted-foreground">
                          <span className="flex items-center gap-1.5">
                            <ShieldCheck className="h-3.5 w-3.5 text-primary" />
                            {step.why}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                            {step.time}
                          </span>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => {
                            if (step.onAction) {
                              step.onAction();
                            } else {
                              navigate(step.route, { state: { returnTo: `/portal/projects/${projectId}/setup`, ...(step.openInviteDialog && { openInviteDialog: true }) } });
                            }
                          }}
                          disabled={step.onAction && submittingProject}
                          variant={done ? 'outline' : 'default'}
                          className={done ? 'border-border text-foreground' : ''}
                        >
                          {step.onAction && submittingProject ? (
                            <><Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />Submitting...</>
                          ) : (
                            <>{done ? 'Review' : step.action}<ArrowRight className="ml-2 h-3.5 w-3.5" /></>
                          )}
                        </Button>
                      </div>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Footer */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.35 }}
        className="flex justify-end pt-2"
      >
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/portal/projects/${projectId}`)}
          className="text-muted-foreground hover:text-foreground"
        >
          Back to Project <ArrowRight className="ml-1 h-3.5 w-3.5" />
        </Button>
      </motion.div>
    </div>
    </>
  );
};

export default PortalProjectSetupGuide;
